/**
 * @file Merkezi GlitchTip / Sentry yapılandırması, kullanıcı ve HTTP bağlamı.
 * @see https://glitchtip.com/documentation
 */
import * as Sentry from '@sentry/react';

const APP_NAME = 'cagrimerkeziui';

/**
 * SDK başlatıldıysa true (VITE_SENTRY_ENABLED=true ve init çalıştı).
 * @returns {boolean}
 */
function isSentryActive() {
    return typeof Sentry.getClient === 'function' && Sentry.getClient() !== null;
}
const HTTP_LOG_MODE = String(import.meta.env.VITE_SENTRY_HTTP_LOG_MODE || 'error').toLowerCase();
const HTTP_SLOW_THRESHOLD_MS = Number(import.meta.env.VITE_SENTRY_HTTP_SLOW_MS || 1500);
const STANDARD_TAGS = {
    layer: {
        ui: 'ui',
        http: 'http',
        domain: 'domain',
    },
    eventType: {
        action: 'action',
        log: 'log',
        error: 'error',
    },
};

/**
 * GlitchTip / Sentry filtreleri için sabit `tags.section` değerleri.
 * Yeni ekranlarda buradan seçin; serbest string kullanmayın.
 */
export const SENTRY_SECTION = Object.freeze({
    auth: 'auth',
    supplier: 'supplier',
    notification: 'notification',
    payment: 'payment',
    callcenter: 'callcenter',
    util: 'util',
    industry: 'industry',
    file: 'file',
    http: 'http',
    ui: 'ui',
    general: 'general',
});

/**
 * Domain aksiyonları — `tags.action` (snake_case, filtreleme için tek tip).
 */
export const SENTRY_ACTION = Object.freeze({
    auth_verify_2fa: 'verify_2fa',
    supplier_get_check: 'get_supplier_check',
    supplier_selection_blocked: 'supplier_selection_blocked',
});
const PII_KEYS = [
    'phone',
    'email',
    'mail',
    'token',
    'password',
    'authorization',
    'address',
];

const getLocalUser = () => {
    const id = localStorage.getItem('idCcnUser');
    if (!id) return null;
    return {
        id: String(id),
        email: localStorage.getItem('mail') || undefined,
        username: localStorage.getItem('nameSurname') || undefined,
    };
};

const normalizeRoute = (route) => {
    if (!route) return '/';
    // Dinamik ID değerlerini tek transaction altında toplamak için normalize et.
    return route.replace(/\/\d{2,}/g, '/:id');
};

const applyGlobalEventContext = (event) => {
    const route = normalizeRoute(window.location?.pathname || '/');
    const user = getLocalUser();
    event.tags = {
        app: APP_NAME,
        env: import.meta.env.MODE || 'unknown',
        route,
        user_id: user?.id || 'anonymous',
        ...event.tags,
    };
    if (!event.user && user) {
        event.user = user;
    }
    return event;
};

const normalizeUrlForTag = (url) => {
    if (!url) return '';
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.pathname.replace(/\/\d{2,}/g, '/:id');
    } catch (e) {
        return String(url).replace(/\/\d{2,}/g, '/:id');
    }
};

const sanitizeData = (input) => {
    if (!input || typeof input !== 'object') return input;
    const out = Array.isArray(input) ? [] : {};
    Object.entries(input).forEach(([key, value]) => {
        const lower = key.toLowerCase();
        if (PII_KEYS.some((k) => lower.includes(k))) {
            out[key] = '[REDACTED]';
            return;
        }
        if (value && typeof value === 'object') {
            out[key] = sanitizeData(value);
        } else {
            out[key] = value;
        }
    });
    return out;
};

const normalizeEndpoint = (url) => normalizeUrlForTag(url || '');

const isSentryEnvelopeUrl = (url) => {
    if (!url) return false;
    try {
        const parsed = new URL(url, window.location.origin);
        return /\/api\/\d+\/envelope\/?/i.test(parsed.pathname);
    } catch (e) {
        const raw = String(url);
        return /\/api\/\d+\/envelope\/?/i.test(raw);
    }
};

const withMandatoryTags = (tags = {}, defaults = {}) => ({
    section: tags.section || defaults.section || 'general',
    action: tags.action || defaults.action || 'unknown',
    ...defaults,
    ...tags,
});

const applyCaptureContext = (scope, context = {}) => {
    const { tags, extra, user, level, contexts } = context;
    if (tags && typeof tags === 'object') {
        Object.entries(tags).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                scope.setTag(key, String(value));
            }
        });
    }
    if (extra && typeof extra === 'object') {
        const safeExtra = sanitizeData(extra);
        Object.entries(extra).forEach(([key, value]) => {
            scope.setExtra(key, safeExtra[key]);
        });
    }
    if (user && typeof user === 'object') {
        scope.setUser(user);
    }
    if (contexts && typeof contexts === 'object') {
        Object.entries(contexts).forEach(([key, value]) => {
            scope.setContext(key, value);
        });
    }
    if (level) {
        scope.setLevel(level);
    }
};

const mapLevelToLoggerMethod = (level) => {
    const l = String(level || 'info').toLowerCase();
    if (l === 'warning') return 'warn';
    if (['trace', 'debug', 'info', 'warn', 'error', 'fatal'].includes(l)) return l;
    return 'info';
};

/**
 * Sentry / GlitchTip kapalı. Başlatma bilinçli olarak devre dışı.
 */
export const initSentry = () => {
    // Kullanılmıyor — SDK başlatılmaz; diğer helper'lar isSentryActive() ile no-op kalır.
    return;
};

/**
 * Sentry kullanıcı bağlamını ayarlar (Issues / Logs ile ilişkilendirme).
 * @param {{ id?: string, email?: string, username?: string } | null} user
 */
export const setSentryUser = (user) => {
    if (!isSentryActive()) return;
    Sentry.setUser(user);
};

/** Oturum kapanınca kullanıcıyı temizler. */
export const clearSentryUser = () => {
    if (!isSentryActive()) return;
    Sentry.setUser(null);
};

/**
 * Yakalanan hatayı GlitchTip’e gönderir.
 * @param {unknown} error
 * @param {object} [context]
 * @param {Record<string, string>} [context.tags]
 * @param {Record<string, unknown>} [context.extra]
 */
export const captureAppException = (error, context = {}) => {
    if (!isSentryActive()) return;
    Sentry.withScope((scope) => {
        const tags = withMandatoryTags(context.tags, {
            layer: STANDARD_TAGS.layer.domain,
            event_type: STANDARD_TAGS.eventType.error,
            action: 'exception',
        });
        applyCaptureContext(scope, { ...context, tags });
        Sentry.captureException(error);
    });
};

/**
 * Bilgi / uyarı mesajı (Issues + SDK Logs pipeline).
 * @param {string} message
 * @param {'info'|'warning'|'error'|'debug'|'fatal'|'trace'} [level='info']
 * @param {object} [context]
 */
export const captureAppMessage = (message, level = 'info', context = {}) => {
    if (!isSentryActive()) return;
    const tags = withMandatoryTags(context.tags, {
        layer: STANDARD_TAGS.layer.domain,
        event_type: STANDARD_TAGS.eventType.log,
        action: 'message',
    });
    Sentry.withScope((scope) => {
        applyCaptureContext(scope, { ...context, level, tags });
        Sentry.captureMessage(message);
    });

    // Logs sekmesi (SDK 10+): structured log envelope — Issues’daki captureMessage’dan ayrı
    const logAttrs = sanitizeData({
        ...tags,
        ...(context.extra && typeof context.extra === 'object' ? context.extra : {}),
    });
    const methodName = mapLevelToLoggerMethod(level);
    const logFn = Sentry.logger?.[methodName];
    if (typeof logFn === 'function') {
        logFn.call(Sentry.logger, message, logAttrs);
    }
};

/**
 * Async işi Sentry span içinde çalıştırır (performans).
 * @param {import('@sentry/core').StartSpanOptions} spanOptions
 * @param {() => void | Promise<unknown>} callback
 */
export const startAppSpan = async (spanOptions, callback) => {
    if (!isSentryActive()) {
        return callback();
    }
    if (typeof Sentry.startSpan === 'function') {
        return Sentry.startSpan(spanOptions, callback);
    }
    return callback();
};

/**
 * @param {import('@sentry/core').Breadcrumb} breadcrumb
 */
export const addAppBreadcrumb = (breadcrumb) => {
    if (!isSentryActive() || typeof Sentry.addBreadcrumb !== 'function') return;
    Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Önemli kullanıcı aksiyonlarını kaydeder.
 * @param {string} action
 * @param {Record<string, unknown>} [data]
 */
export function trackUserAction(action, data = {}) {
    if (!isSentryActive()) return;
    captureAppMessage(`user.action.${action}`, 'info', {
        tags: withMandatoryTags({
            section: SENTRY_SECTION.ui,
            action: String(action).replace(/\s+/g, '_'),
        }),
        extra: sanitizeData(data),
    });
}

/**
 * Sayfa görüntüleme (SPA route adı).
 * @param {string} pageName
 */
export function trackPageView(pageName) {
    if (!isSentryActive()) return;
    const name = String(pageName);
    setSentryTag('page', name);
    captureAppMessage(`page.view ${name}`, 'info', {
        tags: withMandatoryTags({
            section: SENTRY_SECTION.ui,
            action: 'page_view',
            page: name,
        }),
    });
}

/**
 * İsimlendirilmiş ek context (PII sanitize).
 * @param {string} name
 * @param {Record<string, unknown>} context
 */
export function setSentryContext(name, context) {
    if (!isSentryActive()) return;
    Sentry.setContext(name, sanitizeData(context));
}

/**
 * Manuel performans span’i; bitirmek için dönen `finish()` çağrılır.
 * @param {string} name
 * @param {string} [op='custom']
 * @returns {{ finish: () => void, span: import('@sentry/core').Span | undefined }}
 */
export function startTransaction(name, op = 'custom') {
    if (!isSentryActive() || typeof Sentry.startInactiveSpan !== 'function') {
        return { finish: () => {}, span: undefined };
    }
    const span = Sentry.startInactiveSpan({ name, op });
    return {
        finish: () => {
            try {
                span?.end();
            } catch {
                /* ignore */
            }
        },
        span,
    };
}

export const logUiAction = (actionName, data = {}, level = 'info') => {
    if (!isSentryActive()) return;
    const label = typeof data?.label === 'string' ? data.label.trim().replace(/\s+/g, ' ').slice(0, 120) : '';
    const message = label || `ui.action.${actionName}`;
    captureAppMessage(message, level, {
        tags: withMandatoryTags({
            layer: STANDARD_TAGS.layer.ui,
            event_type: STANDARD_TAGS.eventType.action,
            section: SENTRY_SECTION.ui,
            action_name: actionName,
            action: actionName,
            button_label: label || 'unknown',
        }),
        extra: data,
    });
};

export const logHttpSuccess = ({ method, url, status, durationMs }) => {
    if (!isSentryActive()) return;
    if (isSentryEnvelopeUrl(url)) return;
    const requestData = sanitizeData({
        method: (method || 'GET').toUpperCase(),
        url,
        endpoint: normalizeEndpoint(url),
        status,
        durationMs,
    });
    addAppBreadcrumb({
        category: 'http',
        message: `${(method || 'GET').toUpperCase()} ${url || ''}`,
        level: 'info',
        data: requestData,
    });

    const shouldSendInfoEvent = HTTP_LOG_MODE === 'all' || (HTTP_LOG_MODE === 'slow' && Number(durationMs || 0) >= HTTP_SLOW_THRESHOLD_MS);
    if (shouldSendInfoEvent) {
        captureAppMessage(`http.success.${requestData.method} ${requestData.endpoint}`, 'info', {
            tags: withMandatoryTags({
                layer: STANDARD_TAGS.layer.http,
                event_type: STANDARD_TAGS.eventType.log,
                section: SENTRY_SECTION.http,
                action: 'request_success',
                http_method: requestData.method,
                http_status: requestData.status,
                http_endpoint: requestData.endpoint,
            }),
            extra: requestData,
        });
    }
};

export const logHttpError = ({ method, url, status, durationMs }) => {
    if (!isSentryActive()) return;
    if (isSentryEnvelopeUrl(url)) return;
    const requestData = sanitizeData({
        method: (method || 'GET').toUpperCase(),
        url,
        endpoint: normalizeEndpoint(url),
        status,
        durationMs,
    });
    addAppBreadcrumb({
        category: 'http',
        message: `${(method || 'GET').toUpperCase()} ${url || ''}`,
        level: 'error',
        data: requestData,
    });

    const shouldSendErrorEvent = HTTP_LOG_MODE === 'all' || HTTP_LOG_MODE === 'error' || HTTP_LOG_MODE === 'slow';
    if (shouldSendErrorEvent) {
        captureAppMessage(`http.error.${requestData.method} ${requestData.endpoint}`, 'warning', {
            tags: withMandatoryTags({
                layer: STANDARD_TAGS.layer.http,
                event_type: STANDARD_TAGS.eventType.error,
                section: SENTRY_SECTION.http,
                action: 'request_error',
                http_method: requestData.method,
                http_status: requestData.status,
                http_endpoint: requestData.endpoint,
            }),
            extra: requestData,
        });
    }
};

export const sentryStandards = {
    STANDARD_TAGS,
    SECTION: SENTRY_SECTION,
    ACTION: SENTRY_ACTION,
};

/**
 * @param {string} key
 * @param {string|number|boolean} value
 */
export const setSentryTag = (key, value) => {
    if (!isSentryActive() || value === undefined || value === null) return;
    Sentry.setTag(key, String(value));
};

let lastLoggedRouteKey = '';

/** Gezinme etiketi, breadcrumb ve (aktifse) log satırı. */
export const setSentryRouteContext = (pathname) => {
    if (!isSentryActive()) return;
    const normalizedRoute = normalizeRoute(pathname);
    Sentry.setTag('route', normalizedRoute);
    Sentry.setContext('navigation', {
        route: normalizedRoute,
        rawRoute: pathname,
    });
    addAppBreadcrumb({
        category: 'navigation',
        type: 'navigation',
        level: 'info',
        message: `route:${normalizedRoute}`,
    });
    const routeKey = `${normalizedRoute}|${pathname}`;
    if (routeKey !== lastLoggedRouteKey && typeof Sentry.logger?.info === 'function') {
        lastLoggedRouteKey = routeKey;
        Sentry.logger.info('navigation.route_change', {
            section: SENTRY_SECTION.ui,
            action: 'route_change',
            route: normalizedRoute,
            raw_route: pathname,
        });
    }
};

/** Uygulama açılışında Logs doğrulaması için tek satır. */
export const logAppBootstrap = () => {
    if (!isSentryActive() || typeof Sentry.logger?.info !== 'function') return;
    Sentry.logger.info('app.bootstrap', {
        section: SENTRY_SECTION.general,
        action: 'bootstrap',
        app: APP_NAME,
        env: import.meta.env.MODE || 'unknown',
    });
};
